import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@shared/api/client';

/**
 * User existence check response DTO
 */
export interface UserExistsCheckDTO {
  exists: boolean;
  userId?: string;
  email: string;
  message?: string;
  user?: {
    id?: string;
    tenantId?: string;
    email?: string;
    name?: string;
    cognitoId?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
  };
}

/**
 * Configuration for user existence hook
 */
export interface UseUserExistsConfig {
  /** Debounce delay in milliseconds (default: 500) */
  debounceDelay?: number;
  /** Enable/disable the query (default: true) */
  enabled?: boolean;
  /** Custom endpoint for user existence check (default: '/users/check-exists') */
  endpoint?: string;
  /** Custom query parameter name for email (default: 'email') */
  emailParam?: string;
  /** Minimum email length to start checking (default: 3) */
  minEmailLength?: number;
  /** Enable immediate check on mount if email is provided (default: false) */
  checkOnMount?: boolean;
}

/**
 * Return type for useUserExists hook
 */
export interface UseUserExistsReturn {
  /** Whether the user exists */
  exists: boolean | null;
  /** User ID if user exists */
  userId?: string;
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Currently checked email */
  checkedEmail: string;
  /** Function to manually check user existence */
  checkUserExists: (email: string) => void;
  /** Function to clear the current check */
  clearCheck: () => void;
  /** Whether a check has been performed */
  hasChecked: boolean;
  /** Raw API response data */
  data?: UserExistsCheckDTO;
}

/**
 * Consolidated User Existence Hook
 *
 * Provides a unified interface for checking user existence with proper debouncing,
 * caching, and error handling. Replaces multiple different implementations across the app.
 *
 * Features:
 * - Automatic debouncing to prevent excessive API calls
 * - React Query caching for efficient re-checks
 * - Configurable endpoints and parameters
 * - Email validation before making requests
 * - Manual and automatic checking modes
 *
 * @param email - Email address to check (can be empty for manual mode)
 * @param config - Configuration options
 * @returns User existence state and control functions
 */
export function useUserExists(
  email: string = '',
  config: UseUserExistsConfig = {}
): UseUserExistsReturn {
  const {
    debounceDelay = 500,
    enabled = true,
    endpoint = '/users/check-exists',
    emailParam = 'email',
    minEmailLength = 3,
    checkOnMount = false,
  } = config;

  // State for debounced email
  const [debouncedEmail, setDebouncedEmail] = useState<string>('');
  const [hasChecked, setHasChecked] = useState<boolean>(false);

  // Ref for debounce timeout
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Email validation
  const isValidEmail = useCallback(
    (emailToCheck: string): boolean => {
      if (!emailToCheck || emailToCheck.length < minEmailLength) {
        return false;
      }

      // Basic email format validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(emailToCheck);
    },
    [minEmailLength]
  );

  // Debounce email changes
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (isValidEmail(email)) {
      debounceTimeoutRef.current = setTimeout(() => {
        setDebouncedEmail(email);
        setHasChecked(true);
      }, debounceDelay);
    } else {
      setDebouncedEmail('');
      if (email === '') {
        setHasChecked(false);
      }
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [email, debounceDelay, isValidEmail]);

  // Check on mount if configured
  useEffect(() => {
    if (checkOnMount && isValidEmail(email)) {
      setDebouncedEmail(email);
      setHasChecked(true);
    }
  }, [checkOnMount, email, isValidEmail]);

  // React Query for user existence check
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userExists', debouncedEmail],
    queryFn: async (): Promise<UserExistsCheckDTO> => {
      if (!debouncedEmail) {
        throw new Error('No email provided for existence check');
      }

      const params = new URLSearchParams();
      params.append(emailParam, debouncedEmail);

      const url = `${endpoint}?${params.toString()}`;
      const response = await apiClient.get<UserExistsCheckDTO>(url);

      return response.data || { exists: false, email: debouncedEmail };
    },
    enabled: enabled && !!debouncedEmail && isValidEmail(debouncedEmail),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: (failureCount, error: any) => {
      // Don't retry on 4xx errors
      if (error?.status >= 400 && error?.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    // Don't refetch on window focus for user existence checks
    refetchOnWindowFocus: false,
  });

  // Manual check function
  const checkUserExists = useCallback(
    (emailToCheck: string) => {
      if (isValidEmail(emailToCheck)) {
        setDebouncedEmail(emailToCheck);
        setHasChecked(true);

        // If the email is different, trigger a refetch
        if (emailToCheck !== debouncedEmail) {
          // The useEffect will handle setting the debounced email
          // and the query will automatically refetch when the key changes
        } else {
          // If it's the same email, manually refetch
          refetch();
        }
      }
    },
    [debouncedEmail, isValidEmail, refetch]
  );

  // Clear check function
  const clearCheck = useCallback(() => {
    setDebouncedEmail('');
    setHasChecked(false);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  }, []);

  return {
    exists: data?.exists ?? null,
    userId: data?.userId,
    isLoading,
    error: error as Error | null,
    checkedEmail: debouncedEmail,
    checkUserExists,
    clearCheck,
    hasChecked,
    data,
  };
}
